/**
 * Toast -- ephemeral notification orchestrator.
 *
 * Authored markup (one-time setup per page):
 *
 *   1. A container per position you intend to use:
 *      <div class="toast-container is-top-end"
 *           role="region" aria-label="Notifications"
 *           aria-live="polite"></div>
 *
 *   2. (Required for show() only) a template:
 *      <template id="brio-toast">
 *          <output class="toast" role="status">
 *              <!-- optional <svg aria-hidden="true">...</svg> as first child -->
 *              <div class="toast-content">
 *                  <div class="toast-title" data-toast-title hidden></div>
 *                  <p data-toast-body></p>
 *              </div>
 *              <button class="dismiss" type="button" aria-label="Dismiss">&times;</button>
 *              <div class="toast-progress" data-toast-progress hidden></div>
 *          </output>
 *      </template>
 *
 * Three usage paths, all sharing one contract: insert a .toast into
 * a .toast-container; brio handles the rest (auto-dismiss timer,
 * pause-on-hover/focus, animated removal, dismiss-button wiring).
 *
 *   a) Programmatic JS:
 *        import { show } from "./toast.js";
 *        show({ title, body, variant, duration, position });
 *      Clones the template and appends.
 *
 *   b) Server-sent (datastar SSE, htmx swap, Turbo, etc.):
 *        Server returns
 *          <output class="toast is-success" data-duration="5000">
 *              <div class="toast-content">
 *                  <p>Saved.</p>
 *              </div>
 *              <button class="dismiss" type="button" aria-label="Dismiss">&times;</button>
 *          </output>
 *        and your framework swaps it into a .toast-container.
 *      The MutationObserver below detects the insertion and
 *      orchestrates lifecycle. No JS call site on the consumer.
 *
 *   c) Manual DOM:
 *        container.appendChild(toastEl);   // build the element however
 *      Same observer; same lifecycle.
 *
 * data-duration contract (read on insertion):
 *   present, > 0  ->  auto-dismiss after that many ms
 *   present, 0    ->  sticky (manual dismiss only)
 *   absent        ->  5000ms default; 0 (sticky) for .is-danger
 *
 * Programmatic API:
 *   show(opts)        clone template, fill slots, append
 *   adopt(el, opts)   set data-duration, find container, append
 *   dismiss(el)       animated removal
 *
 * Defaults:
 *   variant   "info"
 *   position  "top-end"
 *   duration  5000ms (info / success / warning) | 0 / sticky (danger)
 *
 * Justification for relaxing brio's "no DOM creation in JS" rule:
 *   Toast content is unknown at SSR time -- async errors, copy
 *   confirmations, server-pushed events. Strict orchestration
 *   would push createElement+innerHTML to every consumer,
 *   defeating the point of a notification primitive. Template+
 *   clone respects the spirit of the rule: the <template> is
 *   authored DOM; cloneNode instantiates that authored structure;
 *   slot-filling uses textContent only (no innerHTML, no string-
 *   built HTML). All toast structure remains auditable in the
 *   page's HTML. This relaxation applies to toast specifically,
 *   not as precedent for relaxing elsewhere. The MutationObserver
 *   path means consumers using server-sent toasts don't even need
 *   the relaxation -- their server owns the markup entirely.
 *
 * Load once: importing this module installs a single document-level
 * MutationObserver and a click-delegation listener. Bundle or
 * <script type="module"> a single time per page.
 *
 * TODO(toast,brio-toast-region): convenience custom element that
 *   owns the container + template setup, so consumers can drop in
 *   a single <brio-toast-region position="top-end"></brio-toast-region>
 *   tag instead of wiring container + template by hand.
 * TODO(toast,swipe-dismiss): swipe-to-dismiss touch gesture; opt-in
 *   via a data attribute on the container.
 */

const TEMPLATE_ID = 'brio-toast';
const DEFAULT_DURATION = 5000;
const DEFAULT_POSITION = 'top-end';
const VALID_POSITIONS = new Set([
    'top-end',
    'top-start',
    'top-center',
    'bottom-end',
    'bottom-start',
    'bottom-center',
]);

// Per-toast lifecycle state -- WeakMap so removed toasts get GC'd.
const states = new WeakMap();

function findContainer(position) {
    const el = document.querySelector(`.toast-container.is-${position}`);
    if (!el) {
        throw new Error(
            `[brio-toast] no .toast-container.is-${position} on page. ` +
                `Add <div class="toast-container is-${position}" role="region" ` +
                `aria-label="Notifications" aria-live="polite"></div> to your HTML.`
        );
    }
    return el;
}

function findTemplate() {
    const tpl = document.getElementById(TEMPLATE_ID);
    if (!tpl || tpl.tagName !== 'TEMPLATE') {
        throw new Error(
            `[brio-toast] no <template id="${TEMPLATE_ID}"> on page. ` +
                `See toast.js header for the canonical template markup.`
        );
    }
    return tpl;
}

function resolveDuration(toastEl) {
    if (toastEl.hasAttribute('data-duration')) {
        const n = parseInt(toastEl.getAttribute('data-duration'), 10);
        return Number.isFinite(n) && n >= 0 ? n : DEFAULT_DURATION;
    }
    // Variant-aware default: danger sticky, else 5000ms.
    return toastEl.classList.contains('is-danger') ? 0 : DEFAULT_DURATION;
}

/**
 * Wire lifecycle for a toast that's been inserted into a container.
 * Idempotent: skips toasts already orchestrated or already leaving.
 * Called from the MutationObserver below; never called from show()
 * or adopt() directly -- those just append, the observer takes over.
 */
function orchestrate(toastEl) {
    if (states.has(toastEl)) return;
    if (toastEl.classList.contains('is-leaving')) return;

    const container = toastEl.closest('.toast-container');
    if (!container) return;

    const duration = resolveDuration(toastEl);

    // Sync the progress bar's animation with the timer. The CSS owns
    // the keyframe and pause-on-hover rule; JS only sets duration and
    // toggles [hidden] based on whether the toast auto-dismisses.
    const progressEl = toastEl.querySelector('[data-toast-progress]');
    if (progressEl) {
        if (duration > 0) {
            progressEl.style.animationDuration = `${duration}ms`;
            progressEl.removeAttribute('hidden');
        } else {
            progressEl.setAttribute('hidden', '');
        }
    }

    const state = duration > 0 ? startTimer(toastEl, container, duration) : { cancel() {} };
    states.set(toastEl, state);
}

/**
 * Auto-dismiss timer with pause-on-hover and pause-on-focus. Pairs
 * with the CSS rule that pauses the progress bar's
 * animation-play-state on the container's :hover / :focus-within.
 *
 * Listeners are scoped via AbortController; cancel() removes them
 * so a busy container with many toasts doesn't accumulate handlers.
 */
function startTimer(toastEl, container, duration) {
    const ac = new AbortController();
    let remaining = duration;
    let startedAt = Date.now();
    let timerId = null;
    let paused = false;

    function start() {
        startedAt = Date.now();
        timerId = setTimeout(() => dismiss(toastEl), remaining);
    }
    function pause() {
        if (paused) return;
        paused = true;
        clearTimeout(timerId);
        remaining -= Date.now() - startedAt;
    }
    function resume() {
        if (!paused) return;
        paused = false;
        if (remaining > 0) start();
    }
    function cancel() {
        clearTimeout(timerId);
        ac.abort();
    }

    start();

    const opts = { signal: ac.signal };
    container.addEventListener('pointerenter', pause, opts);
    container.addEventListener('pointerleave', resume, opts);
    container.addEventListener('focusin', pause, opts);
    container.addEventListener('focusout', resume, opts);

    return { cancel };
}

/**
 * Show a toast by cloning <template id="brio-toast"> and filling its
 * slots from opts. Sets data-duration on the cloned element so the
 * observer reads our resolved value. Returns the live toast element.
 */
export function show(opts) {
    if (!opts || typeof opts !== 'object') {
        throw new Error('[brio-toast] show() requires an options object');
    }

    const variant = opts.variant || 'info';
    const position = opts.position || DEFAULT_POSITION;
    const duration =
        opts.duration !== undefined ? opts.duration : variant === 'danger' ? 0 : DEFAULT_DURATION;

    if (!VALID_POSITIONS.has(position)) {
        throw new Error(
            `[brio-toast] invalid position "${position}". ` +
                `Valid: ${[...VALID_POSITIONS].join(', ')}.`
        );
    }

    const tpl = findTemplate();
    const container = findContainer(position);

    // cloneNode(true) instantiates the authored <template> structure.
    // No createElement, no innerHTML; slots filled with textContent only.
    const fragment = tpl.content.cloneNode(true);
    const toastEl = fragment.querySelector('.toast');
    if (!toastEl) {
        throw new Error(`[brio-toast] <template id="${TEMPLATE_ID}"> missing .toast root.`);
    }

    toastEl.classList.add(`is-${variant}`);

    // Danger overrides the polite container's announcement at the
    // per-message level (more-assertive role wins per ARIA spec).
    if (variant === 'danger') {
        toastEl.setAttribute('role', 'alert');
    }

    const titleSlot = toastEl.querySelector('[data-toast-title]');
    if (titleSlot) {
        if (opts.title) {
            titleSlot.textContent = opts.title;
            titleSlot.removeAttribute('hidden');
        } else {
            titleSlot.setAttribute('hidden', '');
        }
    }

    const bodySlot = toastEl.querySelector('[data-toast-body]');
    if (bodySlot && opts.body) {
        bodySlot.textContent = opts.body;
    }

    toastEl.setAttribute('data-duration', String(duration));
    container.appendChild(toastEl);
    // MutationObserver picks up the insertion and orchestrates.

    return toastEl;
}

/**
 * Adopt a pre-built toast element. Sets data-duration if provided,
 * finds the matching container, and appends. The observer takes
 * over from there. Use this when the toast comes from a framework
 * component or a server response and you want to control duration
 * from JS rather than via data-duration in the markup.
 */
export function adopt(toastEl, opts) {
    opts = opts || {};
    const position = opts.position || DEFAULT_POSITION;

    if (!VALID_POSITIONS.has(position)) {
        throw new Error(
            `[brio-toast] invalid position "${position}". ` +
                `Valid: ${[...VALID_POSITIONS].join(', ')}.`
        );
    }

    if (opts.duration !== undefined) {
        toastEl.setAttribute('data-duration', String(opts.duration));
    }

    const container = findContainer(position);
    container.appendChild(toastEl);

    return toastEl;
}

/**
 * Dismiss a toast: cancel its timer, add .is-leaving (CSS exit
 * transition), then remove from DOM on transitionend. A fallback
 * timeout covers cases where transitionend doesn't fire (reduced
 * motion, transition cancelled, etc.).
 */
export function dismiss(toastEl) {
    if (!toastEl || toastEl.classList.contains('is-leaving')) return;

    const state = states.get(toastEl);
    if (state) {
        state.cancel();
        states.delete(toastEl);
    }

    toastEl.classList.add('is-leaving');

    function cleanup() {
        if (toastEl.parentNode) toastEl.remove();
    }
    toastEl.addEventListener('transitionend', cleanup, { once: true });
    setTimeout(cleanup, 1000);
}

// --- Auto-orchestration ---------------------------------------------
//
// Single document-level observer. Any .toast added inside a
// .toast-container -- by show(), an htmx swap, a datastar update,
// a manual appendChild, an SSR initial render -- gets orchestrated.
// Idempotent via the WeakMap state check in orchestrate().

function processNode(node) {
    if (node.nodeType !== 1) return;
    if (node.classList?.contains('toast') && node.closest('.toast-container')) {
        orchestrate(node);
    }
    // Subtree case: container added with toasts already inside, or a
    // wrapper element added containing toasts.
    node.querySelectorAll?.('.toast').forEach((t) => {
        if (t.closest('.toast-container')) orchestrate(t);
    });
}

const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
        for (const node of m.addedNodes) processNode(node);
    }
});

function init() {
    // Initial pass for any toasts present at page load (SSR).
    document.querySelectorAll('.toast-container .toast').forEach(orchestrate);
    observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}

// --- Click delegation for .dismiss inside .toast --------------------
//
// Toasts deliberately avoid [data-dismissible] (which would let
// dismiss.js call .remove() directly, skipping our exit transition
// and timer cleanup). We handle the click locally.

document.addEventListener('click', (e) => {
    const btn = e.target.closest('.dismiss');
    if (!btn) return;
    const toastEl = btn.closest('.toast');
    if (!toastEl || !toastEl.closest('.toast-container')) return;
    e.stopPropagation();
    dismiss(toastEl);
});
